<template>
  <div
    class="product-options"
    :class="{
      [$class('loading')]: loading,
      [$class('hidden')]: product?.hasOnlyDefaultVariant && !bundle,
    }"
  >
    <div
      v-for="option of options"
      :key="option.key"
      class="product-options__option"
    >
      <legend
        v-if="option.display !== 'bundle'"
        class="product-options__option-title text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
      >
        <span v-text="option.name" />
        <span
          v-if="isSizeOption(option.downcase) && portionsString !== ''"
          class="product-options__portion"
        >
          <span v-html="portionsString" />
          <icon-portion />
        </span>
      </legend>

      <div
        v-if="option.display !== 'dropdown' && option.display !== 'bundle'"
        class="product-options__values"
        :class="`product-options__grid-${option.count}`"
      >
        <template v-for="value of option.values" :key="value.key">
          <selection-tab
            v-if="!value.hidden"
            :id="value.key"
            v-model="currentOptions[option.index]"
            class="product-options__value"
            :label="value.value"
            :show-upsizing="
              option.values.filter((optionValue) => !optionValue.hidden)
                .length > 1 &&
              !value.disabled &&
              product.upsizing?.includes(value.value)
            "
            :variant="variant"
            :featured-image="value.featuredImage"
            :loading="loading"
            :name="option.key"
            :portions="value.portions"
            :value="value.value"
            :visually-disabled="value.disabled"
            :show-back-in-stock-email-form="value.showBackInStockEmailForm"
          />
        </template>
      </div>
      <div v-else-if="option.display === 'bundle'">
        <product-bundle-select
          :option="option"
          :loading="loading"
          :product="product"
          @update-bundle-product="handleUpdateOption"
          @update-disabled-status="handleDisableStatus"
        />
      </div>
      <div v-else class="product-options__dropdown">
        <p
          v-if="loading"
          class="product-options__dropdown-placeholder text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
        />

        <select
          v-model="currentOptions[option.index]"
          class="product-options__variant-select text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
          :aria-label="option.name"
        >
          <template v-for="value of option.values" :key="value.key">
            <option
              v-if="!value.hidden && !value.soldOut"
              class="product-options__option text-body-s-regular text-body-m-regular-desktop"
              :value="value.value"
              v-text="value.value"
            />
          </template>

          <optgroup
            v-if="option.availableState.length > 0"
            :label="$string('product.form.sold_out')"
          >
            <template v-for="value of option.availableState" :key="value.key">
              <option
                v-if="!value.hidden"
                class="product-options__option text-body-s-regular text-body-m-regular-desktop"
                :value="value.value"
                v-text="value.value"
              />
            </template>
          </optgroup>
        </select>

        <div class="product-options__dropdown-trigger">
          <div
            class="product-options__option-count text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
            v-html="option.countName"
          />

          <icon-chevron-down />
        </div>
      </div>
    </div>

    <select
      v-model="currentVariantId"
      class="product-options__variant-select"
      :class="$class('hidden')"
      name="id"
      aria-hidden
    >
      <option
        v-for="(variantObject, index) of product.variants"
        :key="getVariantKey(variantObject, index)"
        :value="variantObject.id"
        v-text="`${variantObject.title} - ${variantObject.id}`"
      />
    </select>

    <div
      v-if="hasExtraOptions || (product.hasOnlyDefaultVariant && bundle)"
      class="product-options__extra"
    >
      <div
        v-for="bundleItem of offsetBundle"
        :key="bundleItem.name"
        class="product-options__option"
      >
        <div>
          <product-bundle-select
            :option="bundleItem"
            is-extra-option
            :loading="loading"
            :product="product"
            @update-disabled-status="handleDisableStatus"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Vue: Product options (product-form)
 * -----------------------------------------------------------------------------
 * Product variants split into separate options and values.
 *
 * @param {Boolean} [loading] - Loading state.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object} section - Main product section settings.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 *
 * @emits updateVariant - Emit new variant object.
 *
 */
import { mapState } from 'pinia';
import SelectionTab from '~async/utils/selection-tab/selection-tab';

import IconChevronDown from '~icons/directional-navigation/chevron/down.svg';
import ProductBundleSelect from '~async/product-bundle-select/product-bundle-select.vue';
import IconPortion from '~icons/general/portion.svg';

import { convertToHandle } from '~/helpers/convert';
import { computeDiscountLogic, emitUpdateVariant } from '~/helpers/vue';
import useDiscountCampaignsStore from '~stores/discount-campaigns';

export default {
  name: 'ProductOptions',

  components: {
    SelectionTab,
    IconChevronDown,
    ProductBundleSelect,
    IconPortion,
  },

  props: {
    bundle: {
      type: [Boolean, Object],
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    section: {
      type: Object,
      default: () => ({
        id: '',
        optionStylingTile: [],
        optionStylingImageTile: [],
      }),
      required: true,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    variantFromUrl: {
      type: Boolean,
      default: true,
    },
  },

  emits: {
    'update-variant': emitUpdateVariant,
    'bundle-extra-item': (payload) => typeof payload === 'object',
    'update-bundle-status': (payload) => {
      return typeof payload === 'boolean';
    },
  },

  data() {
    return {
      currentOptions: [],
      currentVariantId: 0,
      bundleProducts: Array(this.bundle + 1).fill({}),
      hasExtraOptions: false,
      bundleStatus: [],
    };
  },

  computed: {
    /**
     * Get offset bundle items.
     * @returns {Array}
     */
    offsetBundle() {
      const leftOverBundle =
        this.options.length === 0
          ? this.bundle
          : this.bundle.slice(this.options.length);

      const offsetBundle = [];
      for (let left = 0; left < leftOverBundle.length; left++) {
        const currentAvailableState = leftOverBundle[
          left
        ].variantsAvailability.find((available) =>
          leftOverBundle[left].variants.some(
            (variant) => available.id === variant.id,
          ),
        );

        const extraOptionMapping = {
          currentOption: leftOverBundle[left].variants[0],
          currentOptionName: leftOverBundle[left].variants[0]?.option1,
          count: 1,
          display: 'bundle',
          downcase: leftOverBundle[left].id.toLowerCase(),
          handle: convertToHandle(leftOverBundle[left].id),
          name: leftOverBundle[left].id,
          key: `${this.section.id}-${this.product.handle}-option-${left}-${convertToHandle(leftOverBundle[left].id)}`,
          availableState: currentAvailableState.availableState,
        };

        offsetBundle.push(extraOptionMapping);
        this.$emit('bundle-extra-item', offsetBundle);
      }

      return offsetBundle;
    },

    /* Map Pinia getters.
     */
    ...mapState(useDiscountCampaignsStore, {
      getCampaignDiscount: 'getCampaignDiscount',
    }),

    /**
     * Get extended options data.
     * @returns {Array}
     */
    options() {
      if (this.loading && (!this.product || this.product.liquid)) {
        return this.getOptionsPlaceholder();
      }

      if (
        (!this.product?.options?.length && !this.bundle) ||
        this.product?.hasOnlyDefaultVariant
      ) {
        return [];
      }

      /**
       * Create deep clone of variants to avoid dirty computed getters.
       */
      const variantsClone = JSON.parse(JSON.stringify(this.product.variants));

      /**
       * Get extended options data.
       */

      return this.product.options?.map((option) => {
        const handle = convertToHandle(option.name);
        let currentBundleOption = false;
        let currentBundle = false;
        let extraBundleInfo = false;

        const downcase = option.name.toLowerCase();
        const key = `${this.section.id}-${this.product.handle}-option-${option.index}-${convertToHandle(option.name)}`;
        const disabled = {};
        const hidden = {};
        let display = 'dropdown';

        if (this.section.optionStylingTile?.includes(downcase)) {
          display = 'tile';
        } else if (this.section.optionStylingImageTile?.includes(downcase)) {
          display = 'image';
        } else if (this.bundle && this.bundle.length > 0) {
          display = 'bundle';
        }

        /**
         * Setup current extended data for values updates.
         */
        const optionObject = {
          ...option,
          disabled,
          display,
          downcase,
          handle,
          hidden,
          key,
          originalValues: option.values,
        };

        const values = this.getOptionValues(optionObject, variantsClone);
        let availableState = this.getSoldOutValues(
          values,
          optionObject.downcase,
        );

        const count = values.filter((value) => !value.hidden).length;
        const countName = this.getOptionCountName(count, downcase);

        if (this.bundle && this.bundle.length > 0) {
          currentBundle = this.bundle.find(
            (bundle) => bundle.id === option.name,
          );

          // for bundles logic to show products that are not part of
          // product property
          if (
            this.product?.options?.length !== this.bundle?.length ||
            (this.product?.options && this.bundle?.length > 0)
          ) {
            this.hasExtraOptions = true;
          }

          currentBundleOption = currentBundle?.variants.find(
            (current) =>
              option.labels.includes(current?.option1) &&
              current.available === true,
          );

          if (!currentBundleOption) {
            currentBundleOption = currentBundle?.variants[0];
          }

          // only available if setup in metaobject, optional values
          extraBundleInfo = {
            name: currentBundle.name,
            type: currentBundle.type,
          };

          // Bundles have no information on variant metafields
          // therefore we get them from the variants in the metaobject
          const getBundleVariantIds = currentBundle.variants.map(
            (variant) => variant.id,
          );

          const filteredAvailability =
            currentBundle.variantsAvailability.filter((variant) =>
              getBundleVariantIds.includes(variant.id),
            );

          availableState = filteredAvailability;
        }

        /**
         * Return extended data.
         */
        return {
          ...optionObject,
          count,
          countName,
          availableState,
          values,
          currentOption: currentBundleOption,
          bundle: currentBundle.variants,
          extraBundleInfo,
        };
      });
    },

    /**
     * Compute size option index.
     * @returns {Number}
     */
    sizeOptionIndex() {
      return this.options.findIndex((option) => {
        return this.$variable('product.sizeOptions').includes(
          option.name.toLowerCase(),
        );
      });
    },

    /**
     * Computes portion string.
     * @returns {String}
     */
    portionsString() {
      if (!this.variant?.price?.amount) {
        return '';
      }

      let price = this.variant?.price?.amount;
      const compareAtPrice = this.variant.compareAtPrice?.amount;

      const discountLogic = computeDiscountLogic({
        onSale: compareAtPrice > price,
        product: this.product,
        variant: this.variant,
      });

      const discount = this.getCampaignDiscount(discountLogic, 'active');

      const discountMultiplier = discount ? discount / 100 : 0;

      price -= parseInt((price * discountMultiplier).toFixed());

      const portionPrice = price / Number(this.variant.portions);

      return portionPrice > 0
        ? this.$string('product.price.portion_html', {
            replace: { amount: this.$formatMoney(portionPrice) },
          })
        : '';
    },
  },

  watch: {
    /**
     * Watch current options to update current variant ID based on choices.
     */
    currentOptions: {
      handler() {
        const variantTitle = this.currentOptions.join(' / ');
        const newVariant = this.getVariantFromProperty(variantTitle, 'title');

        if (newVariant && newVariant.availableState !== 'hidden') {
          this.currentVariantId = newVariant.id;
          return;
        }

        /**
         * If previously selected size doesn't exist then try to find default
         * size value.
         * - Will only select if available.
         */
        if (
          this.currentOptions[this.sizeOptionIndex] !==
          this.$variable('product.defaultSizeValue')
        ) {
          const defaultSizeVariantTitle = this.currentOptions
            .map((currentOption, index) => {
              if (index === this.sizeOptionIndex) {
                return this.$variable('product.defaultSizeValue');
              }

              return currentOption;
            })
            .join(' / ');

          const defaultSizeVariant = this.getVariantFromProperty(
            defaultSizeVariantTitle,
            'title',
          );

          if (defaultSizeVariant && defaultSizeVariant.available) {
            this.currentVariantId = defaultSizeVariant.id;
            return;
          }
        }

        /**
         * If default size value doesn't exist in option then find variants
         * which match the first variant (excluding hidden variants).
         */
        const matchingVariants = this.product.variants
          .filter((variant) => variant.availableState !== 'hidden')
          .filter(
            (variant) =>
              variant.selectedOptions[0].value === this.currentOptions[0],
          );

        /**
         * If exact match then ignore availability and select it.
         */
        const exactMatch = matchingVariants.find((variant) => {
          return variant.selectedOptions.every((selectedOption, index) => {
            return selectedOption.value === this.currentOptions[index];
          });
        });

        if (exactMatch) {
          this.currentVariantId = exactMatch.id;
          return;
        }

        /**
         * If no exact match then use first variant if all unavailable
         * - Otherwise use first available.
         */
        const allUnavailable = matchingVariants.every(
          (variant) => !variant.available,
        );

        const fallbackVariant = allUnavailable
          ? matchingVariants[0]
          : matchingVariants.find((variant) => variant.available);

        if (!fallbackVariant) {
          return;
        }

        this.currentVariantId = fallbackVariant.id;
      },
      deep: true,
    },

    /**
     * Watch currentVariantId to update Canvas variant when select changes.
     */
    currentVariantId() {
      if (this.variant.id === this.currentVariantId) {
        return;
      }

      const variant = this.getVariantFromProperty(this.currentVariantId);

      if (!variant) {
        return;
      }

      this.$emit('update-variant', variant);
    },

    /**
     * Watch product to update selected variant when product data is loaded.
     */
    product() {
      if (this.product.liquid) {
        return;
      }

      this.currentOptions = [];
      this.getCurrentVariant();
    },

    /**
     * Watch variant for updates to update current options.
     */
    variant() {
      if (!this.variant) {
        this.currentVariantId = 0;
      }

      this.getCurrentOptions();
    },
  },

  mounted() {
    this.getCurrentVariant();

    if (
      this.bundle &&
      this.product?.options?.length === 1 &&
      this.product.type === 'bundle'
    ) {
      this.hasExtraOptions = true;
    }
  },

  methods: {
    /**
     * Handle update bundleItem event.
     * @param {Object} bundleItem - Emitted bundleItem object.
     */
    handleUpdateOption(bundleItem, index) {
      this.currentOptions[index] = bundleItem.option1;
      this.selectedVariant = this.variant;
    },

    /**
     * Get option values.
     * @param {Object} option - Current extended option data.
     * @param {Array<Object>} variants - Deep clone of variants.
     * @returns {Array}
     */
    getOptionValues(option, variants) {
      let values = option.values;

      if (this.$variable('product.sizeOptions').includes(option.downcase)) {
        values = this.getSortedContentSizeValues(option.values);
      } else if (
        this.$variable('product.clothingSizeOptions').includes(option.downcase)
      ) {
        values = this.getSortedSizeValues(option.values);
      }

      return values.map((value, index) => {
        const handle = convertToHandle(value);
        const downcase = value.toLowerCase();
        const key = `${option.key}-value-${index}-${handle}`;

        /**
         * Determine value attributes.
         */
        const {
          disabled,
          showBackInStockEmailForm,
          featuredImage,
          hidden,
          portions,
          soldOut,
        } = this.getValueAttributes(value, option, variants);

        return {
          showBackInStockEmailForm,
          downcase,
          disabled,
          featuredImage,
          handle,
          hidden,
          key,
          portions,
          soldOut,
          value,
        };
      });
    },

    /**
     * Check if option handle is a size option.
     * @param {String} optionDowncase - option name lowercase.
     * @returns {Boolean}
     */
    isSizeOption(optionDowncase) {
      return this.$variable('product.sizeOptions').includes(optionDowncase);
    },

    /**
     * Get custom sorted content size values.
     * - Before they're objects.
     * @param {Array<String>} values - Option values.
     * @returns {Array}
     */
    getSortedContentSizeValues(values) {
      const customSort = (a, b) => {
        const indexA = this.$variable('product.variantSorting').indexOf(
          a.toLowerCase(),
        );
        const indexB = this.$variable('product.variantSorting').indexOf(
          b.toLowerCase(),
        );

        return indexA === -1 && indexB === -1
          ? a.toLowerCase().localeCompare(b.toLowerCase())
          : indexA - indexB;
      };

      const clone = JSON.parse(JSON.stringify(values));
      return clone.sort(customSort);
    },

    /**
     * Get custom sorted size (clothing) values.
     * - Before they're objects.
     * @param {Array<String>} values - Option values.
     * @returns {Array}
     */
    getSortedSizeValues(values) {
      const customSort = (a, b) => {
        const indexA = this.$variable('product.sizeOptionSorting').indexOf(
          a.toLowerCase(),
        );
        const indexB = this.$variable('product.sizeOptionSorting').indexOf(
          b.toLowerCase(),
        );

        return indexA === -1 && indexB === -1
          ? a.toLowerCase().localeCompare(b.toLowerCase())
          : indexA - indexB;
      };

      const clone = JSON.parse(JSON.stringify(values));
      return clone.sort(customSort);
    },

    /**
     * Get additional option value attributes based on product filters.
     * - First find variants that match current value.
     * @param {String} value - Current value.
     * @param {Object} option - Current extended option data.
     * @param {Array<Object>} variants - Deep clone of variants.
     * @returns {Object}
     */
    getValueAttributes(value, option, variants) {
      const matchedIndexVariants = variants.filter((variant) => {
        return variant.selectedOptions[option.index].value === value;
      });

      /**
       * Use matched index variant to find top level attributes.
       */
      const soldOut =
        option.index === 0
          ? matchedIndexVariants
              .filter((variant) => variant.availableState !== 'hidden')
              .every((variant) => !variant.available)
          : false;

      const portions =
        option.index === 1
          ? matchedIndexVariants.find(
              (variant) =>
                variant.selectedOptions[0].value === this.currentOptions[0],
            )?.portions
          : '';

      const featuredImage =
        option.display === 'image' ? matchedIndexVariants[0].featuredImage : {};

      /**
       * Find variants that match all other currentOptions.
       */
      const matchedVariants = matchedIndexVariants.filter((variant) => {
        if (option.index === 0) {
          return true;
        }

        return this.currentOptions.every(
          (currentOption, currentOptionIndex) => {
            if (!currentOption || option.index === currentOptionIndex) {
              return true;
            }

            return (
              variant.selectedOptions[currentOptionIndex]?.value ===
              currentOption
            );
          },
        );
      });

      return {
        disabled: matchedVariants.every((variant) => !variant.available),
        showBackInStockEmailForm: matchedVariants.every(
          (variant) => variant.showBackInStockEmailForm,
        ),
        featuredImage,
        hidden: matchedVariants.every(
          (variant) => variant.availableState === 'hidden',
        ),
        portions,
        soldOut,
      };
    },

    /**
     * Get sold out values.
     * - Sort by size sorting if applicable.
     * @param {Array<Object>} values - Option values.
     * @param {String} option - Downcased option name.
     * @returns {Array}
     */
    getSoldOutValues(values, option) {
      const availableState = values.filter((value) => {
        return value.soldOut && !value.hidden;
      });

      if (!availableState.length) {
        return [];
      }

      const customSort = (a, b) => {
        if (this.$variable('product.clothingSizeOptions').includes(option)) {
          const indexA = this.$variable('product.sizeOptionSorting').indexOf(
            a.downcase,
          );
          const indexB = this.$variable('product.sizeOptionSorting').indexOf(
            b.downcase,
          );

          return indexA === -1 && indexB === -1
            ? a.downcase.localeCompare(b.downcase)
            : indexA - indexB;
        }

        return a.downcase.localeCompare(b.downcase);
      };

      return availableState.sort(customSort);
    },

    /**
     * Handles status from bundle products
     * - if one is soldout/hidden/not available we receive a boolean
     *  @param {Boolean} status - Status of bundle product
     */
    handleDisableStatus(status) {
      this.bundleStatus.push(status);
      const isDisabled = this.bundleStatus.some((state) => state === true);

      /**
       * when one of the values is true
       */
      if (isDisabled) {
        this.$emit('update-bundle-status', true);
      }
    },

    /**
     * Compute option count name depending on option handle.
     * @param {Number} count - Number of products.
     * @param {String} optionDowncase - Option name downcased.
     * @returns {String}
     */
    getOptionCountName(count, optionDowncase) {
      const options = {
        pluralise: count,
        replace: { count },
      };

      // Check if it's a size option by name or by variable list
      if (
        optionDowncase.includes('size') ||
        this.$variable('product.sizeOptions').includes(optionDowncase)
      ) {
        return this.$string('product.variant.option_count_size', options);
      }

      if (this.$variable('product.flavourOptions').includes(optionDowncase)) {
        return this.$string('product.variant.option_count', options);
      }

      if (this.$variable('product.colorOptions').includes(optionDowncase)) {
        return this.$string('product.variant.option_count_color', options);
      }

      // For any other option type, use the option name itself
      const optionName =
        optionDowncase.charAt(0).toUpperCase() + optionDowncase.slice(1);
      return this.$string('product.variant.option_count', {
        ...options,
        replace: {
          ...options.replace,
          option: optionName,
        },
      });
    },

    /**
     * Get options placeholder when loading with no existing product data.
     * - Liquid product data doesn't count as it's possibly in the wrong order.
     * @returns {Array}
     */
    getOptionsPlaceholder() {
      if (this.product?.hasOnlyDefaultVariant) {
        return [];
      }

      const hasImageTile = this.product.options.some((option) => {
        return this.section.optionStylingImageTile.includes(
          option.name.toLowerCase(),
        );
      });

      const tileType = hasImageTile ? 'image' : 'tile';

      return [...new Array(2)].map((_, index) => {
        const name = `${this.$string('accessibility.loading')} ${index}`;

        const option = {
          index,
          labels: [...new Array(2)].map(
            (__, labelIndex) => `${name} ${labelIndex}`,
          ),
          name,
          values: [...new Array(2)].map(
            (__, valueIndex) => `${name} ${valueIndex}`,
          ),
        };

        const handle = convertToHandle(option.name);
        const key = `${handle}-key`;

        const values = [...new Array(2)].map((__, valueIndex) => {
          const value = `${this.$string('accessibility.loading')} ${valueIndex}`;
          const valueHandle = convertToHandle(value);
          const valueDowncase = value.toLowerCase();

          return {
            downcase: valueDowncase,
            disabled: false,
            featuredImage: tileType === 'image' ? {} : false,
            handle: valueHandle,
            hidden: false,
            key: valueHandle,
            portions: 30,
            soldOut: false,
            value,
          };
        });

        return {
          ...option,
          count: 2,
          countName: '',
          disabled: {},
          display: index === 0 ? 'dropdown' : tileType,
          downcase: '',
          handle,
          hidden: {},
          key,
          originalValues: values,
          availableState: [],
          validValues: values,
          values,
        };
      });
    },

    /**
     * Get variant key.
     * @param {Object} variant - Current variant.
     * @param {Number} index - Current index.
     * @returns {String}
     */
    getVariantKey(variant, index) {
      return `${this.product?.handle}-option-${index}-${convertToHandle(variant?.title)}`;
    },

    /**
     * Get current variant on page load or product update.
     */
    getCurrentVariant() {
      if (
        !this.product ||
        !this.product?.variants?.length ||
        this.product.liquid
      ) {
        return;
      }

      if (this.$variable('storybook') && this.variant) {
        this.getCurrentOptions();
        return;
      }

      /**
       * Always get current variant from URL.
       */
      let variantFromUrl = this.getVariantFromUrl();
      if (variantFromUrl && this.variantFromUrl) {
        variantFromUrl = this.checkAvailability(variantFromUrl.id);
        this.currentVariantId = variantFromUrl.id;
        this.getCurrentOptions();
        return;
      }

      if (!this.variantFromUrl && this.variant) {
        this.currentVariantId = this.variant.id;
        this.getCurrentOptions();
        return;
      }

      /**
       * Pick the first in stock variant, which is available.
       * - If product has size option then select first available 908g variant.
       * - If no variants are in stock then use first variant.
       */
      let validVariants = this.product?.variants?.filter(
        (variant) =>
          variant.available && variant.availableState === 'available',
      );

      if (this.sizeOptionIndex) {
        validVariants = this.product?.variants?.filter((variant) => {
          return (
            variant.available ||
            variant.selectedOptions[this.sizeOptionIndex]?.value ===
              this.$variable('product.defaultSizeValue')
          );
        });
      }

      /**
       * If no valid variants then select first non-hidden variant.
       * - If a product has no non-hidden variants then it should be hidden.
       */
      let currentVariant = this.product?.variants[0];

      if (validVariants.length > 0) {
        currentVariant = this.checkAvailability(validVariants[0].id);

        if (this.product.type === 'bundle') {
          currentVariant = validVariants[0];
        }
      }

      /**
       * Only set current variant if none of the variant options are excluded
       * from being initially set.
       * - Except where excluded option has only a single value, otherwise if
       *   single value options are not shown the variant will not be
       *   selectable by the user.
       */
      const currentOptions = currentVariant?.selectedOptions.map(
        (selectedOption) => {
          const optionHandle = convertToHandle(selectedOption.name);
          const matchedOption = this.options.find(
            (option) => option.handle === optionHandle,
          );

          const excludedOption =
            this.$variable('product.excludedInitialSelectOptions').includes(
              selectedOption.name,
            ) && matchedOption.values.length > 1;

          return excludedOption ? '' : selectedOption.value;
        },
      );

      const filteredCurrentOptions = currentOptions.filter(Boolean);

      if (
        filteredCurrentOptions?.length !== currentVariant.selectedOptions.length
      ) {
        this.currentOptions = currentOptions;
        return;
      }

      this.currentVariantId = currentVariant?.id;
      this.getCurrentOptions();
    },

    /**
     * Check availability of the variant.
     * - if it's not available, we select the first available variant
     * @param {Number} variantId - id of the variant from the url
     */
    checkAvailability(variantId) {
      const getCurrentVariant = this.product.variants.find(
        (variant) => variant.id === variantId,
      );

      if (
        getCurrentVariant &&
        getCurrentVariant?.availableState === 'available' &&
        getCurrentVariant?.available
      ) {
        return getCurrentVariant;
      }

      // If the selected variant is not available, find the first available variant
      const firstAvailableVariant = this.product.variants.find(
        (variant) =>
          variant.available && variant.availableState === 'available',
      );

      if (firstAvailableVariant) {
        return firstAvailableVariant;
      }

      const allVariantsUnavailable = this.product?.variants.every(
        (variant) =>
          variant.available === false ||
          variant.availableState === 'hidden' ||
          variant.availableState === 'soldOut',
      );

      if (allVariantsUnavailable) {
        return getCurrentVariant;
      }

      return this.product?.variants.find(
        (variant) =>
          variant.availableState === 'available' && variant.available,
      );
    },

    /**
     * Get initial variant from URL parameter.
     * @returns {Object}
     */
    getVariantFromUrl() {
      const params = new URLSearchParams(document.location.search.substring(1));
      const variantId = Number(params.get('variant'));

      // If we're in products_preview, use the selectedOrFirstAvailableVariant
      if (location.pathname.includes('products_preview')) {
        return this.getVariantFromProperty(
          this.product.selectedOrFirstAvailableVariant.id,
        );
      }

      // If there's a variant ID in the URL, check if it's available
      if (variantId) {
        const variant = this.getVariantFromProperty(variantId);
        if (
          variant &&
          variant.available &&
          variant.availableState === 'available'
        ) {
          return variant;
        }

        // If the variant from URL is not available, use the selectedOrFirstAvailableVariant
        return this.getVariantFromProperty(
          this.product.selectedOrFirstAvailableVariant.id,
        );
      }

      // If no variant ID in URL, use the selectedOrFirstAvailableVariant
      return this.getVariantFromProperty(
        this.product.selectedOrFirstAvailableVariant.id,
      );
    },

    /**
     * Get variant object from variant property.
     * @param {String} value - Value to match.
     * @param {String} property - Property to find by.
     * @returns {Object}
     */
    getVariantFromProperty(value, property = 'id') {
      return this.product.variants?.find((variant) => {
        return variant[property] === value;
      });
    },

    /**
     * Get current options.
     */
    getCurrentOptions() {
      if (!this.variant) {
        this.currentOptions = [];
        return;
      }

      this.variant?.selectedOptions?.forEach((selectedOption, index) => {
        this.currentOptions[index] = selectedOption.value;
      });
    },
  },
};
</script>

<style lang="scss">
@import './product-options';
</style>
